import { motion } from "framer-motion";
import { useLocation } from "wouter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { ugandaTours, rwandaTours, kenyaTours, tanzaniaTours } from "@/lib/tourData";
import PageBanner from "@/components/PageBanner";
import OptimizedImage from "@/components/OptimizedImage";

const countries = [
  {
    id: "uganda",
    name: "Uganda",
    tours: ugandaTours
  },
  {
    id: "rwanda",
    name: "Rwanda",
    tours: rwandaTours
  },
  {
    id: "kenya",
    name: "Kenya",
    tours: kenyaTours
  },
  {
    id: "tanzania",
    name: "Tanzania",
    tours: tanzaniaTours
  }
];

const galleryImages = [
  { id: 1, src: "/gorilla.webp", alt: "Gorilla Trekking Adventure", span: "col-span-2" },
  { id: 2, src: "/crested-crane.jpg", alt: "Uganda's National Bird", span: "row-span-2" },
  { id: 3, src: "/chimpanzee.webp", alt: "Chimpanzee Tracking", span: "" },
  { id: 4, src: "/nyunguwemonkey.jpeg", alt: "Monkey in Natural Habitat", span: "col-span-2" },
  { id: 5, src: "/5O5A9186.JPG", alt: "Wildlife Safari", span: "row-span-2" },
  { id: 6, src: "/5O5A0035.png", alt: "Uganda Wildlife", span: "" },
  { id: 7, src: "/murchison-falls.jpg", alt: "Murchison Falls", span: "col-span-2" },
  { id: 8, src: "/Uganda-Wildlife-Safari.png", alt: "Mountain Elephants", span: "" }
];

export default function Destinations() {
  const [_, navigate] = useLocation();

  return (
    <div className="min-h-screen bg-gray-50">
      <PageBanner
        title="Discover East Africa"
        description="Explore our carefully curated selection of tours across the most beautiful destinations in East Africa"
        backgroundImage="/gorilla.webp"
      />

      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="uganda" className="w-full mb-20">
            <TabsList className="flex justify-center mb-8 bg-transparent border-b overflow-x-auto md:overflow-visible">
              {countries.map((country) => (
                <TabsTrigger
                  key={country.id}
                  value={country.id}
                  className="px-4 md:px-8 py-3 text-lg font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none whitespace-nowrap"
                >
                  {country.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {countries.map((country) => (
              <TabsContent key={country.id} value={country.id}>
                <div className="relative px-4 md:px-12">
                  <Carousel
                    opts={{
                      align: "start",
                      loop: true,
                    }}
                    className="w-full"
                  >
                    <CarouselContent className="-ml-4">
                      {country.tours.map((tour) => (
                        <CarouselItem key={tour.id} className="pl-4 basis-full md:basis-1/2 lg:basis-1/3 h-[550px]">
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col h-full"
                          >
                            <div className="relative h-52 min-h-[208px] flex-shrink-0">
                              <OptimizedImage
                                src={tour.image}
                                alt={tour.title}
                                className="w-full h-full"
                                objectFit="cover"
                              />
                              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-md">
                                <span className="text-sm font-medium text-gray-900">
                                  {tour.duration}
                                </span>
                              </div>
                            </div>
                            <div className="p-6 flex flex-col h-full">
                              <h3 className="text-xl font-semibold text-gray-900 mb-2 font-heading line-clamp-2 h-14">
                                {tour.title}
                              </h3>
                              <p className="text-gray-600 text-sm line-clamp-2 mb-4 h-10">
                                {tour.description}
                              </p>
                              <div className="flex items-center justify-between mb-4 mt-auto">
                                <div>
                                  <p className="text-sm text-gray-500">From</p>
                                  <p className="text-lg font-semibold text-primary">
                                    {tour.price}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm text-gray-500">Valid until</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    Dec 31, 2025
                                  </p>
                                </div>
                              </div>
                              <Button
                                className="w-full font-medium group mt-2"
                                onClick={() => navigate(tour.href)}
                              >
                                View Itinerary
                                <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform duration-300" />
                              </Button>
                            </div>
                          </motion.div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <div className="hidden md:block">
                      <CarouselPrevious className="-left-12" />
                      <CarouselNext className="-right-12" />
                    </div>
                    <div className="flex justify-center gap-2 mt-4 md:hidden">
                      <CarouselPrevious className="relative" />
                      <CarouselNext className="relative" />
                    </div>
                  </Carousel>
                </div>
              </TabsContent>
            ))}
          </Tabs>

          {/* Gallery Section */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 font-heading">
                Wildlife Gallery
              </h2>
              <p className="text-lg text-gray-600">
                Explore the incredible wildlife and landscapes of East Africa
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[200px] md:auto-rows-[300px]">
              {galleryImages.map((image, index) => (
                <motion.div
                  key={image.id}
                  className={`relative overflow-hidden rounded-xl group ${image.span}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <OptimizedImage
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full transition-transform duration-700 ease-in-out group-hover:scale-110"
                    objectFit="cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white text-center px-4 text-lg font-medium transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      {image.alt}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}